import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { normalizeTypoPath } from "@/lib/typo-router";

/**
 * Typo-tolerant catch-all. Any unmatched URL is normalized into the
 * nearest logical city/country/tool route — preventing 404s on
 * misspelled long-tail searches (e.g. "/gold-tola-dehlly", "/aj-ka-gold-rat-in-pak").
 *
 * Genuinely unknown URLs now return a real HTTP 404 instead of a
 * soft-404 (200 + noindex), which wasted crawl budget.
 */
export const Route = createFileRoute("/$")({
  beforeLoad: ({ params, location }) => {
    const raw = (params as { _splat?: string })._splat ?? location.pathname.slice(1);
    if (!raw) return;
    const target = normalizeTypoPath(raw);
    // Avoid redirect loop if we'd land on the same place
    if (target && target !== location.pathname) {
      throw redirect({ to: target, replace: true });
    }
    throw notFound();
  },
  head: () => ({
    meta: [{ name: "robots", content: "noindex,follow" }],
  }),
  component: Fallback,
});

function Fallback() {
  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <h1 className="text-2xl font-bold">Page moved</h1>
        <p className="mt-2 text-sm text-muted-foreground">Use the main DrHint tools below.</p>
        <div className="mt-4 flex flex-wrap justify-center gap-3 text-sm">
          <a href="/pakistan" className="underline">Pakistan gold</a>
          <a href="/tools" className="underline">Tools</a>
          <a href="/blog" className="underline">Blog</a>
        </div>
      </div>
    </div>
  );
}